/**
 * Offshore Sails Application Root — OS-INV-04, OS-INV-10, OS-INV-12, OS-INV-13,
 *   OS-INV-14, MADE-292
 *
 * Handles routing for offshore-sails.com / the /os/* path space.
 *
 * Routes
 *   Public:
 *     /            -> HomePage (marketing — MADE-303)
 *     /about       -> AboutPage (marketing — MADE-303)
 *     /sails       -> SailsPage (marketing — MADE-303)
 *     /contact     -> ContactPage (marketing — MADE-304)
 *     /login       -> OffshoreLogin (shared Firebase flow)
 *     /count/:id   -> OsCountModeEntry (MADE-292: verify session → localStorage → ready screen)
 *     /count-mode/ready -> OsCountModeReady (MADE-292: amber idle scan screen)
 *     /pin         -> OsPinEntry (PIN-based device login — NOT Firebase, NOT /os/login)
 *   PIN-gated (session token from /os/pin, managed by OsSessionGuard):
 *     /scan        -> OsScanEntry (type/scan an item code)
 *     /scan/:code/:location -> OsScanItem (per-item-per-location view + mutations)
 *   Protected (Firebase staff auth + 'offshore' tenant claim):
 *     /inventory   -> Inventory (with OsPinAdmin panel) — default post-login destination
 *     /count-sheet -> InventoryCountSheet
 *     /labels      -> OsLabels (printable label grid)
 *     /audit       -> OsAuditDashboard
 *
 * Post-login route choice: '/os/inventory'. Inventory is the primary day-to-day
 * staff view, so landing there after login is the cleanest default (the count
 * sheet is launched on demand from within Inventory / the nav).
 */

import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
// MADE-303/304: Marketing pages — each self-wraps in MarketingLayout (incl. nav/footer/theme)
import { HomePage } from './components/marketing/HomePage';
import { AboutPage } from './components/marketing/AboutPage';
import { SailsPage } from './components/marketing/SailsPage';
import { ContactPage } from './components/marketing/ContactPage';
import { OffshoreLogin } from './components/OffshoreLogin';
import { OffshoreLayout } from './components/OffshoreLayout';
import { OffshoreProtectedRoute } from './components/OffshoreProtectedRoute';
import { OsSessionGuard } from './components/OsSessionGuard';
import { OsPinEntry } from './components/OsPinEntry';
import { OsScanEntry } from './components/OsScanEntry';
import { OsScanItem } from './components/OsScanItem';
import { OsLabels } from './components/OsLabels';
import { OsAuditDashboard } from './components/OsAuditDashboard';
// OS-INV-16: OS-specific inventory components with location awareness.
// The shared Made.bb components remain unaffected for the madebb tenant.
import { OsInventory } from './components/OsInventory';
import OsInventoryCountSheet from './components/OsInventoryCountSheet';
// MADE-292: Count Mode scanner components
import { OsCountModeEntry } from './components/OsCountModeEntry';
import { OsCountModeReady } from './components/OsCountModeReady';
import { TenantProvider } from '../contexts/TenantContext';

// MADE-303: ComingSoon replaced by marketing site pages.

export const OffshoreApp: React.FC = () => {
  return (
    <Routes>
      {/* ── Marketing / Public Routes ── MADE-303 / MADE-304 ──────────── */}
      {/* Each page component self-wraps in MarketingLayout (nav + footer + brand theme) */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/sails" element={<SailsPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/login" element={<OffshoreLogin />} />

      {/* MADE-292: Count Mode entry — scanned from a manager-printed QR.
          Verifies the session, saves CountModeState to localStorage, then
          redirects to /os/count-mode/ready. Public (no PIN required). */}
      <Route
        path="/count/:sessionId"
        element={
          <TenantProvider tenant="offshore">
            <OsCountModeEntry />
          </TenantProvider>
        }
      />

      {/* MADE-292: Amber "scan next item" idle screen between counts.
          Public — OsCountModeReady wraps itself in TenantProvider. */}
      <Route path="/count-mode/ready" element={<OsCountModeReady />} />

      {/* OS-INV-10 — PIN entry (device auth, no Firebase, no session required). */}
      <Route
        path="/pin"
        element={
          <TenantProvider tenant="offshore">
            <OsPinEntry />
          </TenantProvider>
        }
      />

      {/* ── PIN-gated Mobile Routes (OsSessionGuard) ──────────────────── */}
      {/*
       * OsSessionGuard checks localStorage for a valid (non-expired) session token.
       * If missing/expired → redirects to /os/pin?next=<current path>.
       * The actual token enforcement is server-side (x-os-session-token header).
       * MADE-292: OsSessionGuard also renders OsCountModeBanner when count mode is active.
       */}

      {/* OS-INV-12 — Item code entry / location picker (scanner-wedge landing) */}
      <Route
        path="/scan"
        element={
          <TenantProvider tenant="offshore">
            <OsSessionGuard>
              <OsScanEntry />
            </OsSessionGuard>
          </TenantProvider>
        }
      />

      {/* OS-INV-12 — Per-item-per-location view + mutations */}
      <Route
        path="/scan/:code/:location"
        element={
          <TenantProvider tenant="offshore">
            <OsSessionGuard>
              <OsScanItem />
            </OsSessionGuard>
          </TenantProvider>
        }
      />

      {/* ── Firebase-staff-protected Routes ───────────────────────────── */}

      {/* OS-INV-16 — Inventory page */}
      <Route
        path="/inventory"
        element={
          <OffshoreProtectedRoute>
            <OffshoreLayout title="Inventory">
              <OsInventory />
            </OffshoreLayout>
          </OffshoreProtectedRoute>
        }
      />

      {/* OS-INV-22 — Audit manager dashboard */}
      <Route
        path="/audit"
        element={
          <OffshoreProtectedRoute>
            <OffshoreLayout title="Audit">
              <OsAuditDashboard />
            </OffshoreLayout>
          </OffshoreProtectedRoute>
        }
      />

      {/* OS-INV-16 — Count sheet with location picker before session creation. */}
      <Route
        path="/count-sheet"
        element={
          <OffshoreProtectedRoute>
            <OsInventoryCountSheet />
          </OffshoreProtectedRoute>
        }
      />

      {/* OS-INV-13 — Printable label list (staff only) */}
      <Route
        path="/labels"
        element={
          <OffshoreProtectedRoute>
            <OffshoreLayout title="Labels">
              <OsLabels />
            </OffshoreLayout>
          </OffshoreProtectedRoute>
        }
      />

      {/* Redirect any unknown routes to landing */}
      <Route path="*" element={<Navigate to="/os" replace />} />
    </Routes>
  );
};
